import { createContext, use, useEffect, useRef, useState, type ReactNode } from 'react';
import { m } from 'motion/react';
import { Box, Icon, PlainButton, PlainLink, Spinner, Text } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import * as styles from './index.css';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ChainOfThoughtContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null);

function useChainOfThought(): ChainOfThoughtContextValue {
  const context = use(ChainOfThoughtContext);
  if (context == null) {
    throw new Error('Chain of Thought sub-components must be used within <ChainOfThought>');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

// No 'pending': nothing queues steps ahead of running them, and the state's only effect
// was a check-circle on work that hadn't started. Reintroduce it with a glyph, not as a
// fallthrough.
export type StepStatus = 'complete' | 'active' | 'error';

interface ChainOfThoughtProps {
  /** Whether content is actively streaming/executing. Auto-opens on start, auto-closes on finish. */
  isStreaming?: boolean;
  /** Default open state for non-streaming usage. */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function ChainOfThought({ isStreaming = false, defaultOpen = false, children }: ChainOfThoughtProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen || isStreaming);
  const wasStreamingReference = useRef(isStreaming);

  useEffect(() => {
    if (isStreaming) {
      wasStreamingReference.current = true;
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsOpen(true);
    } else if (wasStreamingReference.current) {
      wasStreamingReference.current = false;
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
      setIsOpen(false);
    }
  }, [isStreaming]);

  return (
    <ChainOfThoughtContext.Provider value={{ isOpen, setIsOpen }}>
      <Box className={styles.container}>{children}</Box>
    </ChainOfThoughtContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Status icon (shared by the group header and its steps)
// ---------------------------------------------------------------------------

/**
 * How a step ended. Split from colour deliberately: a step that ran and didn't succeed
 * must not fall through to the check — that reinstates the false success claim TAG-1754
 * was filed about — but it isn't an alarm either, so only 'terminated' takes red
 * (TAG-2077).
 */
export type StepOutcome = 'ok' | 'unsuccessful' | 'terminated';

type StatusColor = 'critical' | 'base' | 'subtle';

// Only a dead exchange is critical: nothing else in a group predicts task failure, and a
// mark that fires on 1 in 5 calls stops carrying information.
function statusColor(status: StepStatus, outcome: StepOutcome): StatusColor {
  return status === 'error' || outcome === 'terminated' ? 'critical' : 'base';
}

// 'unsubscribe' is Pluma's circle-minus — the neutral did-not-succeed mark. Named for its
// first use, not its shape; there is no plain dash glyph in the set.
export const UNSUCCESSFUL_GLYPH: IconName = 'unsubscribe';

// Outcome picks the glyph so the state survives greyscale and colour blindness, which
// colour alone would not (TAG-2077).
function statusGlyph(outcome: StepOutcome, color: StatusColor, icon?: IconName): IconName {
  if (color === 'critical') return 'error';
  if (outcome === 'unsuccessful') return UNSUCCESSFUL_GLYPH;
  return icon ?? 'check-circle';
}

type StatusIconSlotProps = { status: StepStatus; color: StatusColor; outcome: StepOutcome; icon?: IconName };

// Fixed width so the label doesn't shift as the spinner swaps for a glyph.
function StatusIconSlot({ status, color, outcome, icon }: StatusIconSlotProps) {
  return (
    <Box style={{ width: 20, height: 20 }} flexShrink="0" display="flex" alignItems="center" justifyContent="center">
      {status === 'active' ? (
        <Spinner style={{ scale: '0.75' }} />
      ) : (
        <Icon name={statusGlyph(outcome, color, icon)} size="sm" color={color} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Header / Trigger
// ---------------------------------------------------------------------------

interface ChainOfThoughtHeaderProps {
  children?: ReactNode;
  /** Only 'error' shows here, and only for a dead exchange — no tool outcome reddens a
   * group (TAG-2077). Every group ends settled, so a success mark would sit on all of
   * them and say nothing; the label already names what ran. */
  status?: StepStatus;
}

export function ChainOfThoughtHeader({ children = 'Chain of Thought', status }: ChainOfThoughtHeaderProps) {
  const { isOpen, setIsOpen } = useChainOfThought();
  const isError = status === 'error';

  return (
    <Box
      className={styles.trigger}
      onClick={() => setIsOpen(!isOpen)}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsOpen(!isOpen);
        }
      }}
    >
      {isError ? <Icon name="error" size="sm" color="critical" /> : null}
      <Text color={isError ? 'critical' : 'subtle'} size="s">
        {children}
      </Text>
      <m.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <Icon name="chevron-down" size="xs" color="subtle" />
      </m.div>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Content (animated collapse)
// ---------------------------------------------------------------------------

interface ChainOfThoughtContentProps {
  children: ReactNode;
}

export function ChainOfThoughtContent({ children }: ChainOfThoughtContentProps) {
  const { isOpen } = useChainOfThought();

  return (
    <m.div
      initial={false}
      animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0,
      }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      style={{ overflow: 'hidden' }}
    >
      <Box className={styles.stepList}>{children}</Box>
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// Step
// ---------------------------------------------------------------------------

interface ChainOfThoughtStepProps {
  /** How the step ended. Drives the glyph; only 'terminated' also takes red (TAG-2077). */
  outcome?: StepOutcome;
  /** Replaces the success glyph on a settled step, e.g. a magnifier for a search. */
  icon?: IconName;
  /** Step label text. */
  label: string;
  /** Optional inline badge rendered next to the label. */
  badge?: ReactNode;
  /** Optional description below the label. */
  description?: string;
  /** Visual status of this step. */
  status?: StepStatus;
  /** When true, children are collapsed by default behind a "Show details" toggle. */
  expandable?: boolean;
  /** Nested content (sources, images, text). */
  children?: ReactNode;
}

function stepLabelTextAs(status: StepStatus): typeof ShimmerText | 'span' {
  return status === 'active' ? ShimmerText : 'span';
}

function stepDescriptionColor(color: StatusColor): 'critical' | 'subtle' {
  return color === 'critical' ? 'critical' : 'subtle';
}

function stepToggleLabel(isExpanded: boolean): string {
  return isExpanded ? 'Hide details' : 'Show details';
}

export function ChainOfThoughtStep({
  label,
  badge,
  description,
  status = 'complete',
  outcome = 'ok',
  icon,
  expandable = false,
  children,
}: ChainOfThoughtStepProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = children != null;
  const showExpandToggle = expandable && hasChildren;
  const color = statusColor(status, outcome);

  return (
    <Box className={styles.step}>
      <Box className={styles.stepHeader}>
        <StatusIconSlot status={status} color={color} outcome={outcome} icon={icon} />
        <Box display="flex" flexDirection="column" gap="025" flexGrow="1">
          <Box display="flex" alignItems="center" gap="075" flexWrap="wrap">
            <Text as={stepLabelTextAs(status)} color={color} size="s">
              {label}
            </Text>
            {badge}
          </Box>
          {description != null ? (
            <Text color={stepDescriptionColor(color)} size="xs">
              {description}
            </Text>
          ) : null}
        </Box>
        {showExpandToggle ? (
          <PlainButton
            display="flex"
            alignItems="center"
            gap="025"
            flexShrink="0"
            onClick={() => setIsExpanded((previous) => !previous)}
            aria-expanded={isExpanded}
            className={styles.detailsToggle}
          >
            <Text size="xs" color="subtle">
              {stepToggleLabel(isExpanded)}
            </Text>
            <m.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.15 }}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Icon name="chevron-right" size="xs" color="subtle" />
            </m.div>
          </PlainButton>
        ) : null}
      </Box>
      {hasChildren ? (
        expandable ? (
          <m.div
            initial={false}
            animate={{
              height: isExpanded ? 'auto' : 0,
              opacity: isExpanded ? 1 : 0,
            }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box className={styles.stepContent}>{children}</Box>
          </m.div>
        ) : (
          <Box className={styles.stepContent}>{children}</Box>
        )
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sources (badge list)
// ---------------------------------------------------------------------------

interface ChainOfThoughtSourcesProps {
  children: ReactNode;
}

export function ChainOfThoughtSources({ children }: ChainOfThoughtSourcesProps) {
  return <Box className={styles.sourceList}>{children}</Box>;
}

interface ChainOfThoughtSourceProps {
  href?: string;
  children: ReactNode;
}

export function ChainOfThoughtSource({ href, children }: ChainOfThoughtSourceProps) {
  if (href != null) {
    return (
      <PlainLink href={href} isExternal className={styles.sourceBadge}>
        {children}
      </PlainLink>
    );
  }
  return <Box className={styles.sourceBadge}>{children}</Box>;
}
